/**
 * File: preselectedKnowledgeBase.ts
 * Description: Helpers for the knowledge-base documents the user pre-selects
 *   during the welcome flow. The selection is persisted in the user's backend
 *   profile so it follows them across devices; a localStorage copy is kept as
 *   a synchronous cache for consumers (e.g. the context-management screen) and
 *   as an offline fallback.
 * Author: GitHub Copilot
 * Created: 2026-05-31
 * Last Modified: 2026-06-14
 */

import { KnowledgeBaseDocument } from '../services/knowledgeBaseService';
import { UserProfileService } from '../services/userProfileService';

const STORAGE_KEY = 'archie:preselectedKnowledgeBaseDocs';

export const getPreselectedKnowledgeBaseDocuments = (): KnowledgeBaseDocument[] => {
  if (globalThis.window === undefined) {
    return [];
  }
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as KnowledgeBaseDocument[]) : [];
  } catch {
    return [];
  }
};

export const setPreselectedKnowledgeBaseDocuments = (
  documents: KnowledgeBaseDocument[],
): void => {
  if (globalThis.window === undefined) {
    return;
  }
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(documents));
  } catch {
    // localStorage is best-effort; ignore quota errors.
  }
};

export const clearPreselectedKnowledgeBaseDocuments = (): void => {
  if (globalThis.window === undefined) {
    return;
  }
  try {
    globalThis.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Load the user's pre-selected guidelines from their backend profile and
 * refresh the local cache. Falls back to the local cache when the backend is
 * unavailable.
 */
export const loadPreselectedKnowledgeBaseDocuments = async (): Promise<
  KnowledgeBaseDocument[]
> => {
  try {
    const profile = await UserProfileService.getMyProfile();
    const documents = profile.selected_guidelines ?? [];
    setPreselectedKnowledgeBaseDocuments(documents);
    return documents;
  } catch {
    // Backend unavailable: fall back to the last known local selection.
    return getPreselectedKnowledgeBaseDocuments();
  }
};

/**
 * Persist the user's pre-selected guidelines to their backend profile and
 * update the local cache. The local cache is always updated so synchronous
 * consumers stay in sync even if the network call fails.
 */
export const savePreselectedKnowledgeBaseDocuments = async (
  documents: KnowledgeBaseDocument[],
): Promise<void> => {
  setPreselectedKnowledgeBaseDocuments(documents);
  try {
    await UserProfileService.updateSelectedGuidelines(documents);
  } catch {
    // Best-effort: the local cache still reflects the change.
  }
};

